import {
  LocalShipping,
  PowerOff as UnplugIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import {
  Box,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import React from "react";
import { HosDriverProfileDto, Vehicle } from "../../../../api/vehiclesApi";
import { colors } from "../../../../theme/colors";
import { TimeProgressBar } from "../shared/TimeProgressBar";

interface VehicleListProps {
  vehicles: Vehicle[];
  selectedVehicleId: number | null;
  onVehicleSelect: (vehicleId: number) => void;
  hosProfiles?: HosDriverProfileDto[];
  isLoading?: boolean;
  sentinelRef: React.Ref<HTMLLIElement>;
  listRef: React.Ref<HTMLUListElement>;
  isLoadingMore?: boolean;
  hasMore?: boolean;
}

const STATUS_CONFIG = {
  DRIVING: {
    label: "Driving",
    color: "#4CAF50",
    textColor: "#fff",
    bgColorLight: "#4CAF5033",
    bgColorDark: "#4CAF5040",
  },
  SLEEPER: {
    label: "Sleeper",
    color: "#2196F3",
    textColor: "#fff",
    bgColorLight: "#2196F333",
    bgColorDark: "#2196F340",
  },
  OFF_DUTY: {
    label: "Off Duty",
    color: "#9E9E9E",
    textColor: "#fff",
    bgColorLight: "#9E9E9E33",
    bgColorDark: "#9E9E9E40",
  },
  ON_DUTY: {
    label: "On Duty",
    color: "#FF9800",
    textColor: "#fff",
    bgColorLight: "#FF980033",
    bgColorDark: "#FF980040",
  },
  NO_INFORMATION: {
    label: "No HOS Data",
    color: "#78909C",
    textColor: "#fff",
    bgColorLight: "#78909C33",
    bgColorDark: "#78909C40",
  },
};

const VEHICLE_SKELETON_COUNT = 6;

const FaultIndicator: React.FC<{ vehicle: Vehicle }> = ({ vehicle }) => {
  // Check if there are any active lights or faults
  const hasActiveLights =
    vehicle.lastEmissionsLightOn ||
    vehicle.lastProtectLightOn ||
    vehicle.lastStopLightOn ||
    vehicle.lastWarningLightOn ||
    vehicle.lastCheckEngineLightOn ||
    vehicle.lastFaultSpnDescription ||
    vehicle.lastDtcDescription;

  if (!hasActiveLights) return null;

  const tooltipContent = (
    <Stack spacing={0.5}>
      {/* Active Warning Lights Section */}
      <Typography variant="caption" fontWeight="bold">
        Active Warning Lights:
      </Typography>
      {vehicle.lastStopLightOn && (
        <Typography variant="caption" color="error">
          • Stop Light
        </Typography>
      )}
      {vehicle.lastProtectLightOn && (
        <Typography variant="caption" color="warning.main">
          • Protect Light
        </Typography>
      )}
      {vehicle.lastWarningLightOn && (
        <Typography variant="caption" color="warning.main">
          • Warning Light
        </Typography>
      )}
      {vehicle.lastEmissionsLightOn && (
        <Typography variant="caption">• Emissions Light</Typography>
      )}
      {vehicle.lastCheckEngineLightOn && (
        <Typography variant="caption">• Check Engine Light</Typography>
      )}

      {/* Fault Description Section */}
      {(vehicle.lastFaultSpnDescription || vehicle.lastFaultFmiDescription) && (
        <>
          <Typography variant="caption" fontWeight="bold" sx={{ mt: 1 }}>
            Active Fault:
          </Typography>
          {vehicle.lastFaultSpnDescription && (
            <Typography variant="caption">
              System: {vehicle.lastFaultSpnDescription}
            </Typography>
          )}
          {vehicle.lastFaultFmiDescription && (
            <Typography variant="caption">
              Status: {vehicle.lastFaultFmiDescription}
            </Typography>
          )}
        </>
      )}

      {/* DTC Section if present */}
      {(vehicle.lastDtcDescription || vehicle.lastDtcCode) && (
        <>
          <Typography variant="caption" fontWeight="bold" sx={{ mt: 1 }}>
            DTC Information:
          </Typography>
          {vehicle.lastDtcDescription && (
            <Typography variant="caption">
              {vehicle.lastDtcDescription}
            </Typography>
          )}
          {vehicle.lastDtcCode && (
            <Typography variant="caption">
              Code: {vehicle.lastDtcCode}
            </Typography>
          )}
        </>
      )}

      {/* Timestamp if available */}
      {vehicle.lastFaultTime && (
        <Typography variant="caption" sx={{ mt: 1, color: "text.secondary" }}>
          Last Updated: {new Date(vehicle.lastFaultTime).toLocaleString()}
        </Typography>
      )}
    </Stack>
  );

  // Determine icon color based on most severe active light
  const iconColor = vehicle.lastStopLightOn
    ? "error.main"
    : vehicle.lastProtectLightOn || vehicle.lastWarningLightOn
      ? "warning.main"
      : "info.main";

  return (
    <Tooltip
      title={tooltipContent}
      placement="right"
      PopperProps={{
        sx: {
          zIndex: 10000,
        },
        container: document.fullscreenElement || document.body,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", ml: 0.5 }}>
        <WarningIcon
          sx={{
            fontSize: "1rem",
            color: iconColor,
          }}
        />
      </Box>
    </Tooltip>
  );
};

const VehicleHosStatus: React.FC<{
  hosProfile: HosDriverProfileDto | undefined;
}> = ({ hosProfile }) => {
  const statusConfig =
    (hosProfile?.dutyStatus
      ? STATUS_CONFIG[hosProfile.dutyStatus as keyof typeof STATUS_CONFIG]
      : undefined) ?? STATUS_CONFIG.NO_INFORMATION;

  return (
    <Box
      sx={{
        width: "100%",
        borderRadius: 0.75,
        border: `1px solid ${statusConfig.color}40`,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          height: 16,
          bgcolor: statusConfig.color,
          color: statusConfig.textColor,
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "0.6rem",
          fontWeight: 500,
        }}
      >
        {hosProfile ? statusConfig.label : statusConfig.label}
      </Box>
      {hosProfile && (
        <Box sx={{ p: 0.5, pt: 0.375 }}>
          <TimeProgressBar
            label="Break Time"
            value={hosProfile.breakTime}
            color="#2196F3"
            max={8 * 60} // 8h = 480 min
          />
          <TimeProgressBar
            label="Drive Time"
            value={hosProfile.driveTime}
            color="#4CAF50"
            max={11 * 60} // 11h = 660 min
          />
          <TimeProgressBar
            label="Shift Time"
            value={hosProfile.shiftTime}
            color="#FF9800"
            max={14 * 60} // 14h = 840 min
          />
          <TimeProgressBar
            label="Cycle Time"
            value={hosProfile.cycleTime}
            color="#9C27B0"
            max={70 * 60} // 70h = 4200 min
          />
        </Box>
      )}
    </Box>
  );
};

const PROVIDER_CONFIG: Record<string, { label: string; color: string }> = {
  samsara: { label: "Samsara", color: "#12B76A" },
  motive: { label: "Motive", color: "#F2762E" },
};

const TelematicsProviderBadge: React.FC<{ provider: string | null }> = ({
  provider,
}) => {
  if (!provider) return null;

  const config = PROVIDER_CONFIG[provider.toLowerCase()] ?? {
    label: provider,
    color: "#78909C",
  };

  return (
    <Chip
      label={config.label}
      size="small"
      sx={{
        height: 15,
        fontSize: "0.55rem",
        fontWeight: 600,
        letterSpacing: "0.2px",
        borderRadius: 0.5,
        bgcolor: `${config.color}1F`,
        color: config.color,
        border: `1px solid ${config.color}66`,
        "& .MuiChip-label": { px: 0.5 },
      }}
    />
  );
};

const VehicleSkeletonRow: React.FC = () => (
  <ListItem disablePadding>
    <Box sx={{ width: "100%", px: 2, py: 0.75 }}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <Skeleton variant="circular" width={16} height={16} />
          <Skeleton variant="text" width="55%" height={18} />
        </Box>
        <Skeleton variant="text" width="70%" height={16} />
        <Box
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 0.75,
            overflow: "hidden",
          }}
        >
          <Skeleton variant="rectangular" width="100%" height={16} />
          <Box sx={{ p: 0.5 }}>
            {Array.from({ length: 4 }).map((_, index) => (
              <Skeleton
                key={index}
                variant="rounded"
                width="100%"
                height={12}
                sx={{ mb: index === 3 ? 0 : 0.25 }}
              />
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  </ListItem>
);

export const VehicleList: React.FC<VehicleListProps> = ({
  vehicles,
  selectedVehicleId,
  onVehicleSelect,
  hosProfiles = [],
  isLoading = false,
  sentinelRef,
  listRef,
  isLoadingMore = false,
  hasMore = false,
}) => {
  const theme = useTheme();

  if (isLoading) {
    return (
      <List
        ref={listRef}
        aria-label="Loading vehicles"
        sx={{
          flex: 1,
          overflow: "hidden",
          bgcolor:
            theme.palette.mode === "light"
              ? colors.light.background.paper
              : colors.dark.background.paper,
        }}
      >
        {Array.from({ length: VEHICLE_SKELETON_COUNT }).map((_, index) => (
          <VehicleSkeletonRow key={index} />
        ))}
      </List>
    );
  }

  return (
    <List
      ref={listRef}
      sx={{
        flex: 1,
        overflow: "auto",
        bgcolor:
          theme.palette.mode === "light"
            ? colors.light.background.paper
            : colors.dark.background.paper,
      }}
    >
      {vehicles.map((vehicle, idx) => {
        const hosProfile = hosProfiles.find((p) => p.vehicleId === vehicle.id);

        return (
          <ListItem key={vehicle.id} disablePadding>
            <ListItemButton
              selected={vehicle.id === selectedVehicleId}
              onClick={() => onVehicleSelect(vehicle.id)}
              sx={{
                py: 0.75,
                "&.Mui-selected": {
                  backgroundColor:
                    theme.palette.mode === "light"
                      ? colors.light.background.selected
                      : colors.dark.background.selected,
                  "&:hover": {
                    backgroundColor:
                      theme.palette.mode === "light"
                        ? colors.light.background.selectedHover
                        : colors.dark.background.selectedHover,
                  },
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  flex: 1,
                  gap: 0.25,
                }}
              >
                {/* Vehicle Info Row */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <LocalShipping
                    sx={{
                      color:
                        theme.palette.mode === "light"
                          ? colors.light.text.primary
                          : colors.dark.text.primary,
                      fontSize: "0.95rem",
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "0.8rem",
                      fontWeight: 500,
                      color:
                        theme.palette.mode === "light"
                          ? colors.light.text.primary
                          : colors.dark.text.primary,
                      minWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={vehicle.name}
                  >
                    {vehicle.name}
                  </Typography>
                  <FaultIndicator vehicle={vehicle} />
                  {vehicle.gatewayHealthStatus === "Unplugged" && (
                    <Tooltip title={`Gateway unplugged${vehicle.gatewayUnplugDetectedAt ? ` since ${new Date(vehicle.gatewayUnplugDetectedAt).toLocaleString()}` : ""}`}>
                      <Chip
                        icon={<UnplugIcon sx={{ fontSize: "0.75rem !important" }} />}
                        label="UNPLUGGED"
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: "0.65rem",
                          fontWeight: 700,
                          letterSpacing: "0.5px",
                          bgcolor: "#f44336",
                          color: "#fff",
                          border: "2px solid #d32f2f",
                          boxShadow: "0 0 6px rgba(244, 67, 54, 0.5)",
                          "& .MuiChip-icon": { color: "#fff" },
                          animation: "pulse-unplug 2s ease-in-out infinite",
                          "@keyframes pulse-unplug": {
                            "0%, 100%": { opacity: 1, boxShadow: "0 0 6px rgba(244, 67, 54, 0.5)" },
                            "50%": { opacity: 0.7, boxShadow: "0 0 12px rgba(244, 67, 54, 0.8)" },
                          },
                        }}
                      />
                    </Tooltip>
                  )}
                  <Box sx={{ ml: "auto", flexShrink: 0 }}>
                    <TelematicsProviderBadge
                      provider={vehicle.telematicsProvider}
                    />
                  </Box>
                </Box>

                {/* Vehicle Details Row */}
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: "0.7rem",
                    color:
                      theme.palette.mode === "light"
                        ? colors.light.text.secondary
                        : colors.dark.text.secondary,
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {[vehicle.make, vehicle.model, vehicle.year]
                    .filter(Boolean)
                    .join(" • ")}
                </Typography>

                {/* HOS Status Row */}
                <VehicleHosStatus hosProfile={hosProfile} />
              </Box>
            </ListItemButton>
          </ListItem>
        );
      })}
      {(hasMore || isLoadingMore) && (
        <ListItem
          ref={sentinelRef}
          sx={{
            minHeight: 40,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            py: 1,
          }}
        >
          <CircularProgress
            size={14}
            sx={{ visibility: isLoadingMore ? "visible" : "hidden" }}
          />
        </ListItem>
      )}
    </List>
  );
};
